import { Badge, Box, Text, TextProps, VStack } from "@chakra-ui/react";
import type { AuditDiffData } from "gen/model/auditDiffData";
import { GenericTableData } from "gen/model/genericTableData";
import { ModelAuditLogEntryReadData } from "gen/model/modelAuditLogEntryReadData";
import { ModelAuditLogEntryReadDataList } from "gen/model/modelAuditLogEntryReadDataList";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { get, isEmpty } from "lodash-es";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  DescriptionList,
  DescriptionListItem,
} from "shared/components/list/DescriptionList";

export interface AuditingPanelProps {
  apiAuditLogEntryId: string;
  getRow: (id: string) => GenericTableData["rows"][number] | undefined;
  onClose: () => void;
}

const AuditChangeItemText: React.FC<TextProps> = (props) => (
  <Text
    as="span"
    sx={{
      "&:is(ins)": {
        backgroundColor: "green.50",
        textDecoration: "none",
      },
      "&:is(del)": {
        backgroundColor: "red.50",
      },
    }}
    {...props}
  />
);

const AuditChangeItem: React.FC<
  {
    diffType: string | undefined;
    item: string;
  } & TextProps
> = ({ diffType, item, ...props }) =>
  diffType === "json" ? (
    <AuditChangeItemText
      display="inline-block"
      whiteSpace="pre"
      fontFamily="mono"
      {...props}
    >
      {JSON.stringify(JSON.parse(item), null, 2)}
    </AuditChangeItemText>
  ) : (
    <AuditChangeItemText {...props}>{item}</AuditChangeItemText>
  );

const AuditCreateItem: React.FC<{ modelChange: AuditDiffData }> = ({
  modelChange: { diff_type, after },
}) => <AuditChangeItem as="ins" diffType={diff_type} item={after} />;

const AuditEditItem: React.FC<{ modelChange: AuditDiffData }> = ({
  modelChange: { diff_type, before, after },
}) => (
  <>
    <AuditChangeItem as="del" diffType={diff_type} item={before} />
    <Text as="span">{" -> "}</Text>
    <AuditChangeItem as="ins" diffType={diff_type} item={after} />
  </>
);

const AuditDeleteItem: React.FC<{ modelChange: AuditDiffData }> = ({
  modelChange: { diff_type, before },
}) => {
  const { t } = useTranslation(["common", "auditing"]);
  return (
    <>
      <AuditChangeItem as="del" diffType={diff_type} item={before} />
      <Text as="span">{" -> "}</Text>
      <Text as="i" fontWeight="normal" textColor="gray.500">
        {t("auditing:cleared")}
      </Text>
    </>
  );
};

const AuditNoopItem: React.FC<{ modelChange: AuditDiffData }> = ({
  modelChange: { diff_type, after },
}) => <AuditChangeItem diffType={diff_type} item={after} />;

const AuditChange: React.FC<{
  modelChange: AuditDiffData;
}> = ({ modelChange }) => {
  const { audit_type } = modelChange;
  switch (audit_type) {
    case "create":
      return <AuditCreateItem modelChange={modelChange} />;
    case "edit":
      return <AuditEditItem modelChange={modelChange} />;
    case "delete":
      return <AuditDeleteItem modelChange={modelChange} />;
    case "finalize":
      return <AuditEditItem modelChange={modelChange} />;
    case "noop":
      return <AuditNoopItem modelChange={modelChange} />;
    default:
      return null;
  }
};

const AuditEntry: React.FC<{
  entry: ModelAuditLogEntryReadData;
}> = ({ entry }) => {
  const modelChanges = Object.entries(entry.model_changes);
  if (isEmpty(modelChanges)) {
    return null;
  }

  return (
    <Box
      sx={{
        _notLast: {
          pb: "24px",
          borderBottomWidth: "1px",
        },
      }}
    >
      <DescriptionList>
        {modelChanges.map(([fieldName, modelChange]) => (
          <DescriptionListItem term={fieldName} key={fieldName}>
            <AuditChange modelChange={modelChange} />
          </DescriptionListItem>
        ))}
      </DescriptionList>
    </Box>
  );
};

export const AuditingDrawerContent: React.FC<
  AuditingPanelProps & {
    modelAuditLogEntryList: ModelAuditLogEntryReadDataList | undefined;
  }
> = ({ apiAuditLogEntryId, getRow, modelAuditLogEntryList, onClose }) => {
  const { t } = useTranslation(["common", "auditing"]);
  const row = getRow(apiAuditLogEntryId);

  // in the new paginated world this can legitimately be undefined
  // e.g. you scrolled down, opened up a panel on an event, refreshed the page and the row is not in the first batch
  useEffect(() => {
    if (!row) {
      onClose();
    }
  }, [row, onClose]);

  if (!row) {
    return null;
  }

  // FIXME: Use row metadata instead of reading from cells
  const time = (row.cells["time"].value as TextTableCellValue).text || "";
  const action =
    (row.cells["action_type"].value as TextTableCellValue).text || "";
  const ranBy = (row.cells["user"].value as TextTableCellValue).text || "";
  const fundFamily = get(row.cells, "fund_family.value.text") as
    | string
    | undefined;
  const scenario =
    (row.cells["object_event"].value as TextTableCellValue).text || "";

  return (
    <VStack
      spacing="24px"
      alignItems="stretch"
      borderBottomWidth="1px"
      p="24px"
    >
      <VStack spacing="4px" alignItems="flex-start">
        <Text textStyle="overline">{time}</Text>
        <VStack spacing="8px" alignItems="flex-start">
          <Text textStyle="h5">{scenario}</Text>
          {fundFamily && <Badge>{fundFamily}</Badge>}
        </VStack>
      </VStack>
      <DescriptionList size="sm">
        <DescriptionListItem term={t("common:action")}>
          {action}
        </DescriptionListItem>
        <DescriptionListItem term={t("common:user")}>
          {ranBy}
        </DescriptionListItem>
      </DescriptionList>
      {modelAuditLogEntryList?.map((modelAuditLogEntry, index) => (
        <AuditEntry key={index} entry={modelAuditLogEntry} />
      ))}
    </VStack>
  );
};
